import { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { subscribeSaveErrors, dismissSaveError, type SaveError } from '../../lib/saveErrors.js';

// What a refused save looks like when nothing else has claimed it.
//
// Deliberately NOT a toast that fades. A save that did not happen is a fact the
// person has to act on — usually by trying again or by calling somebody — and a
// message that removes itself after four seconds is a message that arrives
// while you are looking at the keyboard. It stays until dismissed.
//
// Fixed to the bottom so it never covers the field that just refused. On a
// phone it spans the width; on a desktop it sits in the corner the eye returns
// to after a click.
export function SaveErrorBanner(): JSX.Element | null {
  const [errors, setErrors] = useState<SaveError[]>([]);
  useEffect(() => subscribeSaveErrors(setErrors), []);

  if (errors.length === 0) return null;
  return (
    <div
      className="fixed bottom-3 inset-x-3 sm:inset-x-auto sm:end-4 sm:max-w-md z-[60] space-y-2 print:hidden"
      role="alert"
      aria-live="assertive"
      dir="rtl"
    >
      {errors.map((e) => (
        <div key={e.id}
          className="flex items-start gap-2 rounded-xl border-2 border-rose-300 bg-rose-50 shadow-lg px-3 py-2.5">
          <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-rose-900">
              {e.kind === 'READ' ? 'לא ניתן לטעון את הנתונים' : 'הפעולה לא נשמרה'}
            </p>
            <p className="text-sm text-rose-800 break-words">{e.message}</p>
          </div>
          <button
            type="button"
            onClick={() => dismissSaveError(e.id)}
            aria-label="סגור"
            // חצי תיקון: `min-h-[24px]` הועלה והרוחב נשכח, והאייקון הוא
            // `w-4`. התוצאה נמדדה 16×24 — עובר בגובה, נופל ברוחב. סף המגע
            // הוא שני הצירים, ואת הכפתור הזה לוחצים כדי לסלק הודעת שגיאה
            // שמכסה תוכן.
            className="text-rose-400 hover:text-rose-700 flex-shrink-0 min-h-[24px] min-w-[24px] inline-flex items-center justify-center cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
